import { useState } from "react";
import { useAuth } from "./hooks/useAuth";
import { useConversations } from "./hooks/useConversations";
import { usePendingTranslationRetry } from "./hooks/usePendingTranslationRetry";
import { globalCSS, T } from "./theme";
import Spinner from "./components/Spinner";
import LoginScreen from "./screens/LoginScreen";
import ConvoListScreen from "./screens/ConvoListScreen";
import NewChatScreen from "./screens/NewChatScreen";
import ChatScreen from "./screens/ChatScreen";

export default function App() {
  const { user, userProfile, loading, signUp, signIn, signOut, requestNotifications, notificationsEnabled } = useAuth();
  const { conversations, createConversation, addParticipants, renameGroup } = useConversations(
    userProfile?.uid
  );
  usePendingTranslationRetry(userProfile?.uid, conversations);
  const [activeScreen, setActiveScreen] = useState("convoList");
  const [activeConvoId, setActiveConvoId] = useState(null);

  return (
    <>
      <style>{globalCSS}</style>
      <div
        style={{
          maxWidth: 430,
          margin: "0 auto",
          height: "100%",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Loading state */}
        {loading && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              background: T.bg0,
              gap: 16,
            }}
          >
            <div
              style={{
                fontFamily: "'Barlow Condensed', sans-serif",
                fontSize: 36,
                fontWeight: 900,
                color: T.text,
              }}
            >
              CREW<span style={{ color: T.orange }}>LINK</span>
            </div>
            <Spinner />
          </div>
        )}

        {/* Not signed in */}
        {!loading && (!user || !userProfile) && (
          <LoginScreen onSignIn={signIn} onSignUp={signUp} />
        )}

        {/* Signed in — screen routing */}
        {!loading && user && userProfile && (
          <>
            {activeScreen === "chat" && activeConvoId && (
              <ChatScreen
                conversationId={activeConvoId}
                currentUser={userProfile}
                addParticipants={addParticipants}
                renameGroup={renameGroup}
                onBack={() => {
                  setActiveConvoId(null);
                  setActiveScreen("convoList");
                }}
              />
            )}

            {activeScreen === "newChat" && (
              <NewChatScreen
                currentUser={userProfile}
                onStartConversation={async (
                  participantUids,
                  participantLanguages,
                  { groupName } = {}
                ) => {
                  const convId = await createConversation(
                    participantUids,
                    participantLanguages,
                    { creatorUid: userProfile.uid, groupName }
                  );
                  setActiveConvoId(convId);
                  setActiveScreen("chat");
                }}
                onBack={() => setActiveScreen("convoList")}
              />
            )}

            {activeScreen === "convoList" && (
              <ConvoListScreen
                conversations={conversations}
                currentUser={userProfile}
                onSelect={(convId) => {
                  setActiveConvoId(convId);
                  setActiveScreen("chat");
                }}
                onNewChat={() => setActiveScreen("newChat")}
                onSignOut={signOut}
                requestNotifications={requestNotifications}
                notificationsEnabled={notificationsEnabled}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}
